import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useActiveTenantId } from '@/store/tenantStore'
import { Badge, Button, Input, Switch, Textarea, useToast } from '@biartbt/biartd'
import { CrudPage } from '@/components/Crud'
import type { RoleItem } from '../api/rolesApi'
import { rolesApi } from '../api/rolesApi'

type RoleForm = {
  code: string
  name: string
  description: string
  isSystemRole: boolean
}

export default function RolesPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const activeTenantId = useActiveTenantId()
  const tenantIdFromUrl = searchParams.get('tenantId')
  const tenantId = tenantIdFromUrl ? Number(tenantIdFromUrl) : activeTenantId
  const { toast } = useToast()

  const stubCreate = async () => {
    toast({
      title: 'Eksik',
      description: "Backend'de rol oluşturma endpoint'i yok. TODO: POST /api/v1/roles (Identity).",
      variant: 'warning',
    })
    throw new Error('Create not implemented')
  }

  const stubUpdate = async () => {
    toast({
      title: 'Eksik',
      description: "Backend'de rol güncelleme endpoint'i yok. TODO: PUT /api/v1/roles/:id (Identity).",
      variant: 'warning',
    })
    throw new Error('Update not implemented')
  }

  const stubRemove = async () => {
    toast({
      title: 'Eksik',
      description: "Backend'de rol silme endpoint'i yok. TODO: DELETE /api/v1/roles/:id (Identity).",
      variant: 'warning',
    })
    throw new Error('Delete not implemented')
  }

  return (
    <>
    <CrudPage<RoleItem, RoleForm, any, any>
      title="Roller"
      subtitle="Tenant rollerini yönet (şu an salt okunur)"
      primaryActionLabel="Yeni Rol"
      extraRowActions={(role) => (
        <Button
          variant="ghost"
          size="icon"
          aria-label={`${role.name} menü yönetimi`}
          title="Menü yönetimi"
          onClick={(e) => {
            e.stopPropagation()
            const query = tenantId != null ? `?tenantId=${tenantId}` : ''
            navigate(`/organization/roles/${role.roleId}/menu-management${query}`)
          }}
        >
          <i className="material-symbols-outlined text-lg">menu_book</i>
        </Button>
      )}
      onExportClick={() =>
        toast({
          title: 'Eksik',
          description: "Backend'de export endpoint'i yok.",
          variant: 'warning',
        })
      }
      onImportClick={() =>
        toast({
          title: 'Eksik',
          description: "Backend'de import endpoint'i yok.",
          variant: 'warning',
        })
      }
      list={async (p) =>
        rolesApi.list({
          tenantId,
          isActive: p.filters.status === 'all' ? null : p.filters.status === 'active',
          includeDeleted: p.filters.deleted === 'show',
          page: p.page,
          pageSize: p.pageSize,
        })
      }
      getRowId={(r) => r.roleId}
      columns={[
        {
          key: 'code',
          header: 'Kod',
          sortable: true,
          widthClassName: 'w-[120px]',
          render: (r) => <span className="font-medium">{r.code ?? '-'}</span>,
        },
        {
          key: 'name',
          header: 'Ad',
          sortable: true,
          widthClassName: 'w-[220px]',
          render: (r) => r.name,
        },
        {
          key: 'description',
          header: 'Açıklama',
          sortable: false,
          render: (r) => <span className="truncate block max-w-[320px]">{r.description ?? '-'}</span>,
        },
        {
          key: 'isSystemRole',
          header: 'Sistem Rolü',
          sortable: false,
          widthClassName: 'w-[100px]',
          render: (r) =>
            r.isSystemRole ? (
              <Badge variant="default">Evet</Badge>
            ) : (
              <Badge variant="secondary">Hayır</Badge>
            ),
        },
        {
          key: 'isActive',
          header: 'Durum',
          sortable: false,
          widthClassName: 'w-[100px]',
          render: (r) =>
            r.isActive !== false ? (
              <Badge variant="success">Aktif</Badge>
            ) : (
              <Badge variant="warning">Pasif</Badge>
            ),
        },
      ]}
      searchPlaceholder="Ara (kod, ad)…"
      filters={[
        {
          id: 'status',
          label: 'Durum',
          value: 'all',
          options: [
            { label: 'Tümü', value: 'all' },
            { label: 'Aktif', value: 'active' },
            { label: 'Pasif', value: 'inactive' },
          ],
        },
        {
          id: 'deleted',
          label: 'Silinenler',
          value: 'hide',
          options: [
            { label: 'Gizle', value: 'hide' },
            { label: 'Göster', value: 'show' },
          ],
          widthClassName: 'min-w-[160px]',
        },
      ]}
      initialPageSize={10}
      modalTitles={{ create: 'Yeni Rol', edit: 'Rolü Düzenle' }}
      getCreateDefaultForm={() => ({
        code: '',
        name: '',
        description: '',
        isSystemRole: false,
      })}
      getEditForm={(r) => ({
        code: r.code ?? '',
        name: r.name ?? '',
        description: r.description ?? '',
        isSystemRole: r.isSystemRole ?? false,
      })}
      validate={(f) => ({
        code: !f.code.trim() ? 'Kod zorunludur' : f.code.trim().length > 50 ? 'Kod en fazla 50 karakter' : undefined,
        name: !f.name.trim() ? 'Rol adı zorunludur' : f.name.trim().length > 255 ? 'Ad en fazla 255 karakter' : undefined,
      })}
      renderForm={({ form, setForm, touched, setTouched, errors, isSubmitting }) => (
        <div className="grid gap-4">
          <Input
            label="Kod"
            value={form.code}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, code: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.code}
            helperText={touched ? errors.code : undefined}
            required
            disabled={isSubmitting}
            placeholder="Rol kodu"
          />
          <Input
            label="Ad"
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, name: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            required
            disabled={isSubmitting}
            placeholder="Rol adı"
          />
          <Textarea
            label="Açıklama"
            value={form.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setForm((s) => ({ ...s, description: e.target.value }))
            }
            disabled={isSubmitting}
            placeholder="Rol açıklaması"
          />
          <Switch
            checked={form.isSystemRole}
            onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, isSystemRole: v }))}
            label="Sistem Rolü"
            disabled={isSubmitting}
          />
        </div>
      )}
      buildCreatePayload={(f) => ({
        tenantId,
        code: f.code.trim(),
        name: f.name.trim(),
        description: f.description.trim() || null,
        isSystemRole: f.isSystemRole,
      })}
      buildUpdatePayload={(f, _item) => ({
        tenantId,
        code: f.code.trim(),
        name: f.name.trim(),
        description: f.description.trim() || null,
        isSystemRole: f.isSystemRole,
      })}
      create={stubCreate}
      update={stubUpdate}
      remove={stubRemove}
    />
    </>
  )
}
